/**
 * Lesende KI der Planungsphase — der reine Teil (Plan Stufe 7).
 *
 * Hier steht alles, was ohne Netz prüfbar ist: Grenzen je Lauf, was an die
 * KI geht, und vor allem die AUSWERTUNG der Antwort. Die KI liefert nur
 * Kandidaten; ein Vorschlag wird daraus erst, wenn
 *  - das Merkmal bzw. die Frage angefragt war und der Wert zum Typ passt,
 *  - das wörtliche Zitat im extrahierten Text steht (Textquelle) bzw. die
 *    Antwort wörtlich in der Mail steht.
 * Bei einem Scan ist das Zitat nicht prüfbar: dort gilt nur ein Ja, nie ein
 * Nein oder eine Zahl — ein falsches Nein ließe eine Frage verschwinden,
 * eine falsche Zahl eine Schwelle der BaustellV kippen.
 *
 * Datenschutz-Regel des Hauses: an die KI geht NUR, was der Nutzer
 * ausdrücklich ausgewählt hat (Dokument bzw. Mailtext) plus die Fragen —
 * kein Projekt-Freitext, keine Personendaten aus dem Projekt. Die Payloads
 * werden deshalb hier gebaut und im Test auf ihre Schlüssel geprüft.
 * Einzige Ausnahme (Betreiberentscheid 29.10.2026): die Baubeschreibung der
 * Grundlagen, nur auf Klick „Baubeschreibung auswerten (KI)", mit Hinweis an
 * der Schaltfläche — und nichts sonst aus dem Projekt.
 */

import {
    enthaeltVerneinung,
    erstelleZitatPruefung,
    findeWoertlichStelle,
    mailSatz,
    normalisiere,
    verneinungAmRand,
    zahlSteht,
} from './zitat';
import type { KiAntwortVorschlag, KiLauf, KiMerkmalAnfrage, KiMerkmalVorschlag, KiQuelle } from './typen';

// ─── Grenzen je Lauf (der Proxy prüft dieselben als zweite Reihe) ─────────

export const KI_MAX_QUELLEN_JE_LAUF = 5;
export const KI_MAX_SEITEN_JE_QUELLE = 120;
/** Text samt Seitenmarken; der Proxy nimmt höchstens 400.000. */
export const KI_MAX_ZEICHEN_JE_QUELLE = 350_000;
/** Scan/Original: 13 MB wie beim Ausschreibungsimport (base64 bleibt unter ~20 MB). */
export const KI_MAX_DATEI_BYTES = 13 * 1024 * 1024;
export const KI_MAX_MERKMALE = 60;
export const KI_MAX_FRAGEN = 80;
export const KI_MAX_MAIL_ZEICHEN = 60_000;
export const KI_MAX_FRAGE_ZEICHEN = 500;
/** Kürzere Mailausschnitte („Ja.", „siehe oben") belegen nichts. Normalisiert gezählt. */
export const KI_MIN_ANTWORT_ZEICHEN = 15;
/** Merkmalsfeld bzw. Fragecode — wie im Proxy (`PLANUNG_SCHLUESSEL`). */
const KI_SCHLUESSEL = /^[A-Za-z0-9_.:-]{1,80}$/;

const zahl = (n: number): string => n.toLocaleString('de-DE');

/** „3–5, 9" statt „3, 4, 5, 9". */
export const seitenListe = (seiten: number[]): string => {
    const sortiert = [...new Set(seiten)].sort((a, b) => a - b);
    const teile: string[] = [];
    for (let i = 0; i < sortiert.length; i++) {
        let j = i;
        while (j + 1 < sortiert.length && sortiert[j + 1] === sortiert[j] + 1) j++;
        teile.push(j > i ? `${sortiert[i]}–${sortiert[j]}` : `${sortiert[i]}`);
        i = j;
    }
    return teile.join(', ');
};

/** Gründe, aus denen ein Kandidat verworfen wird — als Konstanten für die Oberfläche. */
export const KI_VERWORFEN = {
    zitatNichtImDokument: 'Zitat steht nicht im Dokument',
    zitatZuKurz: 'Zitat zu kurz zum Prüfen',
    zitatWeichtAb: 'Zitat weicht vom Dokument ab',
    zahlNichtImZitat: 'Zahl steht nicht im Zitat',
    neinOhneVerneinung: 'Nein ohne Verneinung im Satz',
    ohneZitat: 'Ohne wörtliches Zitat',
    scanNurJa: 'Nein/Zahl nur mit prüfbarem Zitat',
    unbekanntesMerkmal: 'Unbekanntes Merkmal',
    falscherTyp: 'Wert passt nicht zum Typ des Merkmals',
    unbekannteFrage: 'Unbekannte Frage',
    leereAntwort: 'Leere Antwort',
    antwortNichtInMail: 'Antwort steht nicht wörtlich in der Mail',
    antwortZuKurz: 'Antwort zu kurz zum Prüfen',
    antwortAusZitat: 'Ausschnitt aus zitierter früherer Nachricht',
    antwortNurFrage: 'Ausschnitt wiederholt nur die Frage',
    antwortWeichtAb: 'Ausschnitt lässt eine Verneinung weg',
    unlesbarerEintrag: 'Unlesbarer Eintrag in der KI-Antwort',
    merkmalUngueltig: 'Merkmal ohne gültiges Feld, Frage oder Typ — nicht gefragt',
    frageUngueltig: 'Frage ohne gültigen Code oder Text — nicht gefragt',
    neinOhneBegruendung: 'Nein ohne Zitat und ohne Begründung',
    neinNurMitZitat: 'Nein ohne Zitat — bei diesem Merkmal nicht zulässig',
    begruendungMitVorschrift: 'Begründung nennt eine Vorschrift',
} as const;

/** Sammelt verworfene Kandidaten je Grund, in der Reihenfolge des ersten Auftretens. */
export class Verwerfliste {
    private readonly gruende = new Map<string, number>();

    merke(grund: string, anzahl: number = 1): void {
        if (anzahl <= 0) return;
        this.gruende.set(grund, (this.gruende.get(grund) ?? 0) + anzahl);
    }

    liste(): { grund: string; anzahl: number }[] {
        return [...this.gruende].map(([grund, anzahl]) => ({ grund, anzahl }));
    }
}

export const lauf = <T>(vorschlaege: T[], verworfen: Verwerfliste, fehler: string | null): KiLauf<T> => ({
    vorschlaege: fehler ? [] : vorschlaege,
    verworfen: verworfen.liste(),
    fehler,
});

// ─── Antwort der KI lesen ─────────────────────────────────────────────────

/**
 * Die Liste unter `schluessel` aus der Proxy-Antwort. Der Proxy liefert
 * normalerweise das JSON-Objekt; ist es unlesbar, kommt `{ text }`, bei
 * abgeschnittener Antwort zusätzlich `_truncated` (bei Arrays `{ items }`).
 */
export const leseKiListe = (
    roh: unknown,
    schluessel: string,
): { liste: unknown[] | null; abgeschnitten: boolean } => {
    if (Array.isArray(roh)) return { liste: roh, abgeschnitten: false };
    if (!roh || typeof roh !== 'object') return { liste: null, abgeschnitten: false };
    const obj = roh as Record<string, unknown>;
    const abgeschnitten = obj._truncated === true;
    if (Array.isArray(obj[schluessel])) return { liste: obj[schluessel] as unknown[], abgeschnitten };
    if (Array.isArray(obj.items)) return { liste: obj.items, abgeschnitten };
    if (typeof obj.text === 'string') {
        try {
            const geparst: unknown = JSON.parse(obj.text.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, ''));
            const innen = leseKiListe(geparst, schluessel);
            return { liste: innen.liste, abgeschnitten: abgeschnitten || innen.abgeschnitten };
        } catch {
            return { liste: null, abgeschnitten };
        }
    }
    return { liste: null, abgeschnitten };
};

const alsEintrag = (e: unknown): Record<string, unknown> | null =>
    e && typeof e === 'object' && !Array.isArray(e) ? (e as Record<string, unknown>) : null;

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

// ─── Merkmale ─────────────────────────────────────────────────────────────

/** Ein Dokument, wie es an die KI geht. `seiten` = die gesendeten Seiten (Prüfgrundlage). */
export interface MerkmalAuftrag {
    quelle: KiQuelle;
    seiten: { seite: number; text: string }[];
}

export interface MerkmalPlan {
    auftraege: MerkmalAuftrag[];
    merkmale: KiMerkmalAnfrage[];
    fehler: string | null;
}

export type MerkmalPayload = {
    dokument: string;
    quelle: { art: 'text'; text: string } | { art: 'pdf'; base64Data: string; mimeType: string };
    merkmale: KiMerkmalAnfrage[];
};

/** Text mit Seitenmarken `[[Seite n]]`, wie ihn der Proxy-Prompt beschreibt. */
export const textMitSeitenmarken = (seiten: { seite: number; text: string }[]): string =>
    seiten.map((s) => `[[Seite ${s.seite}]]\n${s.text}`).join('\n\n');

const bereinigeMerkmale = (merkmale: KiMerkmalAnfrage[], verworfen: Verwerfliste): KiMerkmalAnfrage[] => {
    const gesehen = new Set<string>();
    const gueltig: KiMerkmalAnfrage[] = [];
    for (const m of merkmale ?? []) {
        const feld = text(m?.feld);
        const frage = text(m?.frage);
        const typ = m?.typ;
        if (!KI_SCHLUESSEL.test(feld) || !frage || frage.length > KI_MAX_FRAGE_ZEICHEN || (typ !== 'ja_nein' && typ !== 'zahl')) {
            verworfen.merke(KI_VERWORFEN.merkmalUngueltig);
            continue;
        }
        if (gesehen.has(feld)) continue;
        gesehen.add(feld);
        gueltig.push({ feld, frage, typ });
    }
    if (gueltig.length > KI_MAX_MERKMALE) {
        verworfen.merke(`Mehr als ${KI_MAX_MERKMALE} Merkmale je Lauf — Rest nicht gefragt`, gueltig.length - KI_MAX_MERKMALE);
        gueltig.length = KI_MAX_MERKMALE;
    }
    return gueltig;
};

/** Welche Dokumente und Seiten gehen an die KI? Was nicht geht, steht in `verworfen`. */
export const planeMerkmalLauf = (
    quellen: KiQuelle[],
    merkmale: KiMerkmalAnfrage[],
    verworfen: Verwerfliste,
): MerkmalPlan => {
    const gefragt = bereinigeMerkmale(merkmale, verworfen);
    if (!quellen?.length) return { auftraege: [], merkmale: gefragt, fehler: 'Kein Dokument ausgewählt.' };
    if (!gefragt.length) return { auftraege: [], merkmale: gefragt, fehler: 'Keine Merkmale zum Nachlesen ausgewählt.' };

    if (quellen.length > KI_MAX_QUELLEN_JE_LAUF) {
        const rest = quellen.slice(KI_MAX_QUELLEN_JE_LAUF).map((q) => `„${q.dokument}"`).join(', ');
        verworfen.merke(`Mehr als ${KI_MAX_QUELLEN_JE_LAUF} Dokumente je Lauf — nicht gelesen: ${rest}`, quellen.length - KI_MAX_QUELLEN_JE_LAUF);
    }

    const auftraege: MerkmalAuftrag[] = [];
    for (const quelle of quellen.slice(0, KI_MAX_QUELLEN_JE_LAUF)) {
        const name = `„${quelle.dokument}"`;
        if (quelle.verworfen) {
            verworfen.merke(`${name}: ${quelle.verworfen}`);
            continue;
        }
        if (quelle.art === 'text') {
            // Seiten ohne Text gehen im Textweg nicht mit — das muss sichtbar sein.
            const ohneText = quelle.ohneTextSeiten ?? [];
            if (ohneText.length) {
                verworfen.merke(`${name}: ${ohneText.length === 1 ? 'Seite' : 'Seiten'} ${seitenListe(ohneText)} ohne lesbaren Text — nicht gelesen`, ohneText.length);
            }
            const mitText = (quelle.seiten ?? []).filter((s) => s.text.trim() !== '');
            if (!mitText.length) {
                verworfen.merke(`${name}: kein lesbarer Text`);
                continue;
            }
            const gesendet: { seite: number; text: string }[] = [];
            let zeichen = 0;
            for (const s of mitText) {
                const laenge = `[[Seite ${s.seite}]]\n${s.text}`.length + 2;
                if (gesendet.length >= KI_MAX_SEITEN_JE_QUELLE || zeichen + laenge > KI_MAX_ZEICHEN_JE_QUELLE) break;
                gesendet.push(s);
                zeichen += laenge;
            }
            const rest = mitText.length - gesendet.length;
            if (!gesendet.length) {
                verworfen.merke(`${name}: Seite ${mitText[0].seite} allein ist zu lang für die KI`);
                continue;
            }
            if (rest > 0) {
                verworfen.merke(
                    `${name}: Seiten ab Seite ${mitText[gesendet.length].seite} nicht gelesen `
                    + `(höchstens ${KI_MAX_SEITEN_JE_QUELLE} Seiten bzw. ${zahl(KI_MAX_ZEICHEN_JE_QUELLE)} Zeichen je Dokument)`,
                    rest,
                );
            }
            auftraege.push({ quelle, seiten: gesendet });
        } else {
            const daten = quelle.datei?.daten;
            if (!daten?.length) {
                verworfen.merke(`${name}: Original fehlt — Scan nicht lesbar`);
                continue;
            }
            if (daten.length > KI_MAX_DATEI_BYTES) {
                verworfen.merke(`${name}: zu groß für die KI (höchstens 13 MB)`);
                continue;
            }
            auftraege.push({ quelle, seiten: [] });
        }
    }
    if (!auftraege.length) {
        return { auftraege, merkmale: gefragt, fehler: 'Keines der ausgewählten Dokumente konnte an die KI gehen — Gründe unter „verworfen".' };
    }
    return { auftraege, merkmale: gefragt, fehler: null };
};

/** base64 ohne Data-URL-Vorspann, in Stücken (ein Spread über 13 MB sprengt den Stack). */
export const zuBase64 = (bytes: Uint8Array): string => {
    let binaer = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binaer += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    }
    return btoa(binaer);
};

/** Payload für `extractPlanungMerkmale` — NUR Dokumentname, Dokument und Fragen. */
export const baueMerkmalPayload = (auftrag: MerkmalAuftrag, merkmale: KiMerkmalAnfrage[]): MerkmalPayload => {
    const { quelle } = auftrag;
    const fragen = merkmale.map(({ feld, frage, typ }) => ({ feld, frage, typ }));
    if (quelle.art === 'text') {
        return { dokument: quelle.dokument, quelle: { art: 'text', text: textMitSeitenmarken(auftrag.seiten) }, merkmale: fragen };
    }
    const datei = quelle.datei!;
    return {
        dokument: quelle.dokument,
        quelle: { art: 'pdf', base64Data: zuBase64(datei.daten), mimeType: datei.mimeType },
        merkmale: fragen,
    };
};

type RoherMerkmalVorschlag = Omit<KiMerkmalVorschlag, 'id'>;

/**
 * Antwort der KI zu EINEM Dokument auswerten. `fehler` heißt: die Antwort
 * war unlesbar — dann gilt der ganze Lauf als gescheitert, nie als
 * „nichts gefunden".
 */
export const werteMerkmalAntwortAus = (
    roh: unknown,
    auftrag: MerkmalAuftrag,
    merkmale: KiMerkmalAnfrage[],
    verworfen: Verwerfliste,
): { vorschlaege: RoherMerkmalVorschlag[]; fehler: string | null } => {
    const { dokument } = auftrag.quelle;
    const { liste, abgeschnitten } = leseKiListe(roh, 'merkmale');
    if (!liste) return { vorschlaege: [], fehler: `Die Antwort der KI zu „${dokument}" war nicht lesbar — bitte noch einmal versuchen.` };

    const nachFeld = new Map(merkmale.map((m) => [m.feld, m]));
    const pruefe = auftrag.quelle.art === 'text' ? erstelleZitatPruefung(auftrag.seiten) : null;
    const beantwortet = new Set<string>();
    const vorschlaege: RoherMerkmalVorschlag[] = [];

    for (const roherEintrag of liste) {
        const e = alsEintrag(roherEintrag);
        if (!e) {
            verworfen.merke(KI_VERWORFEN.unlesbarerEintrag);
            continue;
        }
        const feld = text(e.feld);
        const merkmal = nachFeld.get(feld);
        if (!merkmal) {
            verworfen.merke(KI_VERWORFEN.unbekanntesMerkmal);
            continue;
        }
        beantwortet.add(feld);
        const wert = e.wert;
        if (wert === null || wert === undefined) continue;
        const typPasst = merkmal.typ === 'ja_nein'
            ? typeof wert === 'boolean'
            : typeof wert === 'number' && Number.isFinite(wert) && wert >= 0;
        if (!typPasst) {
            verworfen.merke(KI_VERWORFEN.falscherTyp);
            continue;
        }
        const zitat = text(e.zitat);
        if (!zitat) {
            verworfen.merke(KI_VERWORFEN.ohneZitat);
            continue;
        }
        const wertGeprueft = wert as boolean | number;

        if (pruefe) {
            // Textquelle: das Zitat muss an Wortgrenzen im gesendeten Text
            // stehen, ohne weggelassene Verneinung daneben. Seite und Zitat
            // kommen aus dem DOKUMENT (ganzer Satz), nicht aus der KI.
            const fund = pruefe(zitat);
            // `in` statt `!fund.ok`: das Projekt läuft ohne strictNullChecks,
            // dort trennt TypeScript die Union nicht am booleschen Merkmal.
            if ('grund' in fund) {
                verworfen.merke(
                    fund.grund === 'zu_kurz' ? KI_VERWORFEN.zitatZuKurz
                        : fund.grund === 'abweichung' ? KI_VERWORFEN.zitatWeichtAb
                            : KI_VERWORFEN.zitatNichtImDokument,
                );
                continue;
            }
            // Eine Zahl gilt nur, wenn sie als Zahlwort in der zitierten Stelle
            // selbst steht — nicht irgendwo im Satzfenster (Tabellenzeilen
            // ohne Punkt laufen sonst in die Nachbarzeile).
            if (typeof wertGeprueft === 'number' && !zahlSteht(wertGeprueft, fund.treffer)) {
                verworfen.merke(KI_VERWORFEN.zahlNichtImZitat);
                continue;
            }
            // Ein Nein blendet Fragen aus — es gilt nur, wenn die zitierte
            // Stelle SELBST eine Verneinung enthält. Der Satz reicht nicht: dort
            // kann eine fremde stehen („…, Abbrucharbeiten sind dagegen nicht
            // vorgesehen", Überschrift „Abbruch entfällt", Seitenfuß „Keine …").
            if (wertGeprueft === false && !enthaeltVerneinung(fund.treffer)) {
                verworfen.merke(KI_VERWORFEN.neinOhneVerneinung);
                continue;
            }
            vorschlaege.push({ feld, wert: wertGeprueft, zitat: fund.dokumentZitat, seite: fund.seite, dokument, geprueft: true });
        } else {
            // Scan: nicht prüfbar — nur ein Ja darf als Vorschlag erscheinen.
            if (wertGeprueft !== true) {
                verworfen.merke(KI_VERWORFEN.scanNurJa);
                continue;
            }
            const seite = typeof e.seite === 'number' && Number.isInteger(e.seite) && e.seite >= 1 ? e.seite : null;
            vorschlaege.push({ feld, wert: true, zitat, seite, dokument, geprueft: false });
        }
    }

    if (abgeschnitten) {
        verworfen.merke(
            `„${dokument}": Antwort der KI abgeschnitten — Merkmale ohne Antwort bitte selbst prüfen`,
            Math.max(1, merkmale.length - beantwortet.size),
        );
    }
    return { vorschlaege, fehler: null };
};

/**
 * Vorschläge aller Dokumente zusammenführen. Gleicher Wert zum selben Feld →
 * EIN Vorschlag (erstes Zitat; ein geprüftes schlägt ein ungeprüftes aus
 * einem Scan). Verschiedene Werte → beide bleiben, die Oberfläche zeigt den
 * Widerspruch; der zweite Wert bekommt die Kennung `ki:${feld}:2`.
 */
export const fasseMerkmaleZusammen = (roh: RoherMerkmalVorschlag[]): KiMerkmalVorschlag[] => {
    const nachFeld = new Map<string, RoherMerkmalVorschlag[]>();
    for (const v of roh) {
        const liste = nachFeld.get(v.feld) ?? [];
        const gleich = liste.findIndex((x) => x.wert === v.wert);
        if (gleich < 0) liste.push(v);
        else if (!liste[gleich].geprueft && v.geprueft) liste[gleich] = v;
        nachFeld.set(v.feld, liste);
    }
    return [...nachFeld].flatMap(([feld, liste]) =>
        liste.map((v, i) => ({ id: i === 0 ? `ki:${feld}` : `ki:${feld}:${i + 1}`, ...v })));
};

// ─── Baubeschreibung (Einschätzung) ───────────────────────────────────────

/** Baubeschreibung samt ergänzenden Angaben; der Proxy nimmt höchstens dasselbe. */
export const KI_MAX_BESCHREIBUNG_ZEICHEN = 30_000;
/** Längere Begründungen werden gekürzt — sie sind Hinweis, kein Beleg. */
export const KI_MAX_BEGRUENDUNG_ZEICHEN = 300;
/** So heißt die Quelle in der Leiste („KI · Baubeschreibung (Grundlagen)"). */
export const BAUBESCHREIBUNG_DOKUMENT = 'Baubeschreibung (Grundlagen)';

/**
 * Merkmale, die eine Baubeschreibung nicht ausschließen KANN: wer auf der
 * Baustelle arbeitet, ob der Boden belastet ist, ob Gefahrstoffe nach
 * Anhang II Nr. 2 vorkommen. Ein Nein dazu gilt nur mit Zitat — eine
 * Einschätzung „klingt nicht danach" ließe genau die Fragen verschwinden, die
 * der Bauherr beantworten muss.
 */
export const KI_NEIN_NUR_MIT_ZITAT: readonly string[] = [
    'mehrereArbeitgeber', 'unternehmerOhneBeschaeftigte', 'altlastenverdacht', 'gefahrstoffeAnhang2',
];

/** Eine Begründung, die eine Vorschrift nennt, ist verdächtig (Regelwerks-Regel des Hauses). */
const NENNT_VORSCHRIFT = /§|\b(?:DIN|TRGS|TRBS|ASR|DGUV|BaustellV|ArbSchG|GefStoffV|BetrSichV|RAB|VOB|StVO|RSA)\b/;

export type EinschaetzungPayload = { text: string; merkmale: KiMerkmalAnfrage[] };

export interface EinschaetzungPlan {
    payload: EinschaetzungPayload | null;
    /** Die Baubeschreibung als Textquelle mit einer Seite — Prüfgrundlage der Zitate. */
    auftrag: MerkmalAuftrag | null;
    merkmale: KiMerkmalAnfrage[];
    fehler: string | null;
}

/** Was an die KI geht: NUR der Text der Baubeschreibung und die offenen Merkmale. */
export const planeBeschreibungLauf = (
    text: string,
    merkmale: KiMerkmalAnfrage[],
    verworfen: Verwerfliste,
): EinschaetzungPlan => {
    const t = typeof text === 'string' ? text.trim() : '';
    const gefragt = bereinigeMerkmale(merkmale, verworfen);
    const leer = (fehler: string): EinschaetzungPlan => ({ payload: null, auftrag: null, merkmale: gefragt, fehler });
    if (!t) return leer('Keine Baubeschreibung eingetragen.');
    if (t.length > KI_MAX_BESCHREIBUNG_ZEICHEN) {
        return leer(`Die Baubeschreibung ist zu lang (${zahl(t.length)} Zeichen, höchstens ${zahl(KI_MAX_BESCHREIBUNG_ZEICHEN)}).`);
    }
    if (!gefragt.length) return leer('Alle Merkmale sind gesetzt — es gibt nichts einzuschätzen.');
    const seiten = [{ seite: 1, text: t }];
    const quelle: KiQuelle = { dokument: BAUBESCHREIBUNG_DOKUMENT, art: 'text', seiten, seitenGesamt: 1, ohneTextSeiten: [] };
    return {
        payload: { text: t, merkmale: gefragt.map(({ feld, frage, typ }) => ({ feld, frage, typ })) },
        auftrag: { quelle, seiten },
        merkmale: gefragt,
        fehler: null,
    };
};

/**
 * Antwort der KI zur Baubeschreibung auswerten. Zwei Wege:
 *  - Alles mit Zitat und jedes Ja bzw. jede Zahl läuft durch dieselbe
 *    strenge Prüfung wie bei Unterlagen (`werteMerkmalAntwortAus`): Zitat
 *    wörtlich im Text, Zahl im Zitat, Nein nur mit Verneinung im Zitat.
 *  - Ein Nein OHNE Zitat gilt als Einschätzung — nur mit Begründung, ohne
 *    Vorschrift darin und nicht bei `KI_NEIN_NUR_MIT_ZITAT`.
 * Belegte Vorschläge stehen vor geschätzten: beim Zusammenfassen gewinnt so
 * das Zitat, wenn beide dasselbe Nein sagen.
 */
export const werteEinschaetzungAus = (
    roh: unknown,
    auftrag: MerkmalAuftrag,
    merkmale: KiMerkmalAnfrage[],
    verworfen: Verwerfliste,
): { vorschlaege: RoherMerkmalVorschlag[]; fehler: string | null } => {
    const { liste, abgeschnitten } = leseKiListe(roh, 'merkmale');
    if (!liste) return { vorschlaege: [], fehler: 'Die Antwort der KI zur Baubeschreibung war nicht lesbar — bitte noch einmal versuchen.' };

    const nachFeld = new Map(merkmale.map((m) => [m.feld, m]));
    const streng: unknown[] = [];
    const geschaetzt: RoherMerkmalVorschlag[] = [];
    const beantwortet = new Set<string>();

    for (const roherEintrag of liste) {
        const e = alsEintrag(roherEintrag);
        if (e) beantwortet.add(text(e.feld));
        if (!e || e.wert !== false || text(e.zitat)) {
            streng.push(roherEintrag);
            continue;
        }
        const feld = text(e.feld);
        const merkmal = nachFeld.get(feld);
        if (!merkmal) { verworfen.merke(KI_VERWORFEN.unbekanntesMerkmal); continue; }
        if (merkmal.typ !== 'ja_nein') { verworfen.merke(KI_VERWORFEN.falscherTyp); continue; }
        if (KI_NEIN_NUR_MIT_ZITAT.includes(feld)) { verworfen.merke(KI_VERWORFEN.neinNurMitZitat); continue; }
        const begruendung = text(e.begruendung).replace(/\s+/g, ' ');
        if (!begruendung) { verworfen.merke(KI_VERWORFEN.neinOhneBegruendung); continue; }
        if (NENNT_VORSCHRIFT.test(begruendung)) { verworfen.merke(KI_VERWORFEN.begruendungMitVorschrift); continue; }
        const kurz = begruendung.length > KI_MAX_BEGRUENDUNG_ZEICHEN
            ? `${begruendung.slice(0, KI_MAX_BEGRUENDUNG_ZEICHEN - 1).trimEnd()}…`
            : begruendung;
        geschaetzt.push({ feld, wert: false, zitat: '', seite: null, dokument: auftrag.quelle.dokument, geprueft: false, einschaetzung: kurz });
    }

    // Die strenge Prüfung meldet die Kappung selbst — hier mit der Zahl ALLER
    // beantworteten Felder, nicht nur der belegten.
    const belegt = werteMerkmalAntwortAus({ merkmale: streng }, auftrag, merkmale, verworfen);
    if (belegt.fehler) return { vorschlaege: [], fehler: belegt.fehler };
    if (abgeschnitten) {
        verworfen.merke(
            'Antwort der KI zur Baubeschreibung abgeschnitten — Merkmale ohne Antwort bitte selbst prüfen',
            Math.max(1, merkmale.length - beantwortet.size),
        );
    }
    return { vorschlaege: [...belegt.vorschlaege, ...geschaetzt], fehler: null };
};

// ─── Mailantworten ────────────────────────────────────────────────────────

export type AntwortPayload = { mailText: string; fragen: { code: string; frage: string }[] };

/** Prüft Mailtext und Fragen vor dem Aufruf. */
export const planeMailLauf = (
    mailText: string,
    fragen: { code: string; frage: string }[],
    verworfen: Verwerfliste,
): { payload: AntwortPayload | null; fehler: string | null } => {
    if (typeof mailText !== 'string' || !mailText.trim()) return { payload: null, fehler: 'Kein Mailtext eingefügt.' };
    if (mailText.length > KI_MAX_MAIL_ZEICHEN) {
        verworfen.merke(`Mailtext zu lang (höchstens ${zahl(KI_MAX_MAIL_ZEICHEN)} Zeichen)`);
        return {
            payload: null,
            fehler: `Der Mailtext ist zu lang (${zahl(mailText.length)} Zeichen, höchstens ${zahl(KI_MAX_MAIL_ZEICHEN)}) — bitte nur die Antwort ohne zitierten Verlauf einfügen.`,
        };
    }
    const gesehen = new Set<string>();
    const gueltig: { code: string; frage: string }[] = [];
    for (const f of fragen ?? []) {
        const code = text(f?.code);
        const frage = text(f?.frage);
        if (!KI_SCHLUESSEL.test(code) || !frage || frage.length > KI_MAX_FRAGE_ZEICHEN) {
            verworfen.merke(KI_VERWORFEN.frageUngueltig);
            continue;
        }
        if (gesehen.has(code)) continue;
        gesehen.add(code);
        gueltig.push({ code, frage });
    }
    if (gueltig.length > KI_MAX_FRAGEN) {
        verworfen.merke(`Mehr als ${KI_MAX_FRAGEN} offene Fragen je Lauf — Rest nicht zugeordnet`, gueltig.length - KI_MAX_FRAGEN);
        gueltig.length = KI_MAX_FRAGEN;
    }
    if (!gueltig.length) return { payload: null, fehler: 'Keine offenen Fragen zum Zuordnen.' };
    // NUR Mailtext und Fragen — nichts aus dem Projekt.
    return { payload: { mailText, fragen: gueltig }, fehler: null };
};

/** Kopfzeilen einer eingefügten Mail (deutsch/englisch). */
const KOPFZEILE = /^\s*(?:von|an|cc|bcc|gesendet|betreff|datum|wichtigkeit|priorität|anlagen?|from|to|sent|subject|date|importance|attachments?)\s*:/i;
/** Starke Kopfzeilen: eine davon nach „Von:" belegt einen Mailkopf. */
const STARKE_KOPFZEILE = /^\s*(?:gesendet|datum|sent|date)\s*:/i;
/** Schwache Kopfzeilen: erst zwei davon nach „Von:" belegen einen Mailkopf („An: Samstagen …" allein nicht). */
const SCHWACHE_KOPFZEILE = /^\s*(?:an|cc|betreff|to|subject)\s*:/i;
/** „Von: Name <adresse>" bzw. „Von: adresse" — die ganze Zeile ist Absender, kein Satz. */
const VON_ABSENDER = /^\s*(?:von|from)\s*:\s*(?:[^<>@\n]{0,80}<[^<>\s]+@[^<>\s]+>|"?[^\s<>@]+@[^\s<>@]+"?)\s*$/i;
/** Trennzeile vor einer zitierten oder weitergeleiteten Nachricht. */
const VERLAUF_TRENNZEILE = /^\s*-{2,}\s*(?:ursprüngliche nachricht|original message|weitergeleitete nachricht|forwarded message)/i;
/** Betreffzeile über einem eingefügten Kopf („AW: Klärungsliste …"). */
const BETREFFZEILE = /^\s*(?:aw|re|wg|fw|fwd|antw)\s*:/i;
/** Datum oder Uhrzeit — „Am 16.10.2026 um 10:00 schrieb …", „On Fri, Oct 17, 2026 … wrote:". */
const DATUM_ODER_UHRZEIT = /\d{1,2}\.\s?\d{1,2}\.|\d{1,2}:\d{2}|\d{1,2}\.\s?(?:jan|feb|mär|apr|mai|jun|jul|aug|sep|okt|nov|dez)|\b\d{4}\b/i;

/**
 * „Am <Datum> … schrieb <Name>:" bzw. „On <Datum> … wrote:" — auch über zwei
 * Zeilen umbrochen (Gmail). Verlangt Datum oder Uhrzeit UND einen Abschluss
 * mit Doppelpunkt oder Adresse; „Am 15.10. schrieb der Statiker, dass …"
 * ist ein Satz.
 */
const istZitierkopf = (zeilen: string[], i: number): boolean => {
    const zeile = zeilen[i];
    if (!/^\s*(?:am|on)\s/i.test(zeile) || !DATUM_ODER_UHRZEIT.test(zeile)) return false;
    const kopf = /\b(?:schrieb|wrote)\b/i.test(zeile) ? zeile : `${zeile} ${zeilen[i + 1] ?? ''}`;
    if (!/\b(?:schrieb|wrote)\b/i.test(kopf)) return false;
    return /:\s*$/.test(kopf) || /[<@]/.test(kopf);
};

/**
 * Beginnt bei Zeile `i` der zitierte Verlauf (frühere Nachricht)? „Von:" nur
 * im Kopfmuster — Absenderzeile mit Adresse, oder gefolgt von Gesendet/Datum
 * bzw. zwei von An/Cc/Betreff —, „Am … schrieb"/„On … wrote" nur als
 * Zitierkopf. Sonst ist es eigener Text („Von: 7:00 Uhr", „Von: Herrn
 * Müller <…> kam die Freigabe", „Am Montag schrieb der Statiker, …").
 */
const istVerlaufsbeginn = (zeilen: string[], i: number): boolean => {
    const zeile = zeilen[i];
    if (VERLAUF_TRENNZEILE.test(zeile)) return true;
    if (/^\s*(?:von|from)\s*:/i.test(zeile)) {
        if (VON_ABSENDER.test(zeile)) return true;
        const folgend = zeilen.slice(i + 1, i + 5);
        return folgend.some((z) => STARKE_KOPFZEILE.test(z)) || folgend.filter((z) => SCHWACHE_KOPFZEILE.test(z)).length >= 2;
    }
    return istZitierkopf(zeilen, i);
};

/**
 * Bereich des EIGENEN Texts der Mail: nach dem Kopf ganz oben (Betreffzeile,
 * Von/An/Betreff der eingefügten Mail selbst, eingerückte Folgezeilen) und
 * vor dem zitierten Verlauf. Kopfzeilen und Mailadressen landen so nie in
 * einer Antwort.
 */
export const eigenerTextBereich = (mail: string): { anfang: number; ende: number } => {
    const zeilen = mail.split('\n');
    let pos = 0;
    let anfang = 0;
    let imKopf = true;
    for (let i = 0; i < zeilen.length; i++) {
        const zeile = zeilen[i];
        const kopfartig = zeile.trim() === '' || KOPFZEILE.test(zeile) || BETREFFZEILE.test(zeile)
            || (i > 0 && /^\s+\S/.test(zeile) && KOPFZEILE.test(zeilen[i - 1]));
        if (imKopf && kopfartig) {
            pos += zeile.length + 1;
            continue;
        }
        if (imKopf) {
            anfang = Math.min(pos, mail.length);
            imKopf = false;
        }
        if (istVerlaufsbeginn(zeilen, i)) return { anfang, ende: pos };
        pos += zeile.length + 1;
    }
    return { anfang: imKopf ? mail.length : anfang, ende: mail.length };
};

/** Wo endet der eigene Text der Mail? Ab der ersten Verlaufszeile ist alles zitierter Verlauf. */
export const endeEigenerText = (mail: string): number => eigenerTextBereich(mail).ende;

/** Berührt [von, bis) eine zitierte Zeile („> …")? */
const beruehrtZitatzeile = (mail: string, von: number, bis: number): boolean => {
    let anfang = mail.lastIndexOf('\n', von - 1) + 1;
    while (anfang < bis) {
        const ende = mail.indexOf('\n', anfang);
        const zeile = mail.slice(anfang, ende < 0 ? mail.length : ende);
        if (/^\s*>/.test(zeile)) return true;
        if (ende < 0) break;
        anfang = ende + 1;
    }
    return false;
};

/** Wiederholt der Ausschnitt nur die Frage (samt Code), ohne eigene Aussage? */
export const wiederholtNurFrage = (ausschnitt: string, frage: string, code: string): boolean => {
    let rest = normalisiere(ausschnitt);
    const f = normalisiere(frage);
    if (f && f.includes(rest)) return true;
    if (f) rest = rest.split(f).join(' ');
    const c = normalisiere(code);
    if (c) rest = rest.split(c).join(' ');
    return !/[\p{L}\p{N}]{2,}/u.test(rest);
};

/** Antwort der KI zur Mail auswerten: jeder Ausschnitt muss wörtlich im EIGENEN Text der Mail stehen. */
export const werteMailAntwortAus = (
    roh: unknown,
    payload: AntwortPayload,
    verworfen: Verwerfliste,
): { vorschlaege: KiAntwortVorschlag[]; fehler: string | null } => {
    const { liste, abgeschnitten } = leseKiListe(roh, 'antworten');
    if (!liste) return { vorschlaege: [], fehler: 'Die Antwort der KI war nicht lesbar — bitte noch einmal versuchen.' };

    const { mailText } = payload;
    const bereich = eigenerTextBereich(mailText);
    const imEigenenText = (von: number, bis: number) =>
        von >= bereich.anfang && bis <= bereich.ende && !beruehrtZitatzeile(mailText, von, bis);
    const fragen = new Map(payload.fragen.map((f) => [f.code, f.frage]));
    const codes = new Set(fragen.keys());
    const gesehen = new Set<string>();
    const jeCode = new Map<string, number>();
    const vorschlaege: KiAntwortVorschlag[] = [];

    for (const roherEintrag of liste) {
        const e = alsEintrag(roherEintrag);
        if (!e) {
            verworfen.merke(KI_VERWORFEN.unlesbarerEintrag);
            continue;
        }
        const code = text(e.code);
        if (!codes.has(code)) {
            verworfen.merke(KI_VERWORFEN.unbekannteFrage);
            continue;
        }
        const antwort = text(e.antwort);
        if (!antwort) {
            verworfen.merke(KI_VERWORFEN.leereAntwort);
            continue;
        }
        if (normalisiere(antwort).length < KI_MIN_ANTWORT_ZEICHEN) {
            verworfen.merke(KI_VERWORFEN.antwortZuKurz);
            continue;
        }
        // Der Vorschlag trägt den Wortlaut der MAIL, nicht die Abschrift der KI —
        // und nur aus dem eigenen Text, nie aus dem zitierten Verlauf.
        const fund = findeWoertlichStelle(antwort, mailText, { zulassen: imEigenenText });
        if (!fund) {
            verworfen.merke(findeWoertlichStelle(antwort, mailText) ? KI_VERWORFEN.antwortAusZitat : KI_VERWORFEN.antwortNichtInMail);
            continue;
        }
        // Wie im Dokument: eine weggelassene Verneinung direkt daneben macht
        // den Ausschnitt falsch, und zurück geht der ganze Satz (so steht ein
        // „Wir können nicht bestätigen, dass …" mit im Vorschlag).
        if (verneinungAmRand(mailText, fund.von, fund.bis, true, bereich)) {
            verworfen.merke(KI_VERWORFEN.antwortWeichtAb);
            continue;
        }
        const ausMail = mailSatz(mailText, fund.von, fund.bis, bereich);
        if (wiederholtNurFrage(ausMail, fragen.get(code) ?? '', code)) {
            verworfen.merke(KI_VERWORFEN.antwortNurFrage);
            continue;
        }
        const schluessel = JSON.stringify([code, normalisiere(ausMail)]);
        if (gesehen.has(schluessel)) continue;
        gesehen.add(schluessel);
        const nr = (jeCode.get(code) ?? 0) + 1;
        jeCode.set(code, nr);
        vorschlaege.push({ id: nr === 1 ? `ki:${code}` : `ki:${code}:${nr}`, code, antwort: ausMail, geprueft: true });
    }

    if (abgeschnitten) verworfen.merke('Antwort der KI abgeschnitten — nicht alle Fragen zugeordnet');
    return { vorschlaege, fehler: null };
};
